import { useCallback, useEffect, useState } from 'react';
import { Plus, Play, Trash2, Save, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import Button from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import irrigationNetworkService, {
  type IrrigationScenario,
  type IrrigationNode,
  type SolveResult,
  type SolveWarning,
} from '../../../services/irrigationNetwork';
import { errorMessage } from '../../../utils/apiResponse';

interface Props {
  farmId: number;
  nodes: IrrigationNode[];
  activeScenarioId: number | null;
  setActiveScenarioId: (id: number | null) => void;
  draftOpenValves: Set<number>;
  setDraftOpenValves: (s: Set<number>) => void;
  result: SolveResult | null;
  setResult: (r: SolveResult | null) => void;
  onJump: (target: { node_id?: number; pipe_id?: number }) => void;
}

export default function ScenariosPanel({
  farmId, nodes, activeScenarioId, setActiveScenarioId,
  draftOpenValves, setDraftOpenValves, result, setResult, onJump,
}: Props) {
  const [scenarios, setScenarios] = useState<IrrigationScenario[]>([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [solving, setSolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valves = nodes.filter(n => n.node_type === 'valve');

  const loadScenarios = useCallback(async () => {
    try {
      setScenarios(await irrigationNetworkService.listScenarios(farmId));
    } catch (e) { setError(errorMessage(e, 'Failed to load scenarios')); }
  }, [farmId]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const activate = (sc: IrrigationScenario | null) => {
    setActiveScenarioId(sc?.id ?? null);
    setDraftOpenValves(new Set(sc?.open_valve_node_ids ?? []));
    setResult(null);
  };

  const toggleValve = (id: number) => {
    const next = new Set(draftOpenValves);
    if (next.has(id)) next.delete(id); else next.add(id);
    setDraftOpenValves(next);
    setResult(null);
  };

  const solve = async () => {
    setSolving(true);
    setError(null);
    try {
      const res = await irrigationNetworkService.solve(farmId, {
        open_valve_node_ids: Array.from(draftOpenValves),
      });
      setResult(res);
    } catch (e) { setError(errorMessage(e, 'Solver failed')); }
    finally { setSolving(false); }
  };

  const saveDraft = async () => {
    setError(null);
    try {
      if (activeScenarioId) {
        await irrigationNetworkService.updateScenario(activeScenarioId, {
          open_valve_node_ids: Array.from(draftOpenValves),
        });
      } else {
        if (!newName.trim()) { setError('Name required'); return; }
        const sc = await irrigationNetworkService.createScenario(farmId, {
          name: newName.trim(),
          open_valve_node_ids: Array.from(draftOpenValves),
        });
        setActiveScenarioId(sc.id);
        setNewName('');
        setCreating(false);
      }
      await loadScenarios();
    } catch (e) { setError(errorMessage(e, 'Save failed')); }
  };

  const remove = async (id: number) => {
    if (!confirm('Delete scenario?')) return;
    try {
      await irrigationNetworkService.deleteScenario(id);
      if (activeScenarioId === id) activate(null);
      await loadScenarios();
    } catch (e) { setError(errorMessage(e, 'Delete failed')); }
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">Scenarios</h3>
          {!creating && (
            <button className="text-xs flex items-center gap-1 text-blue-600"
              onClick={() => { setCreating(true); activate(null); }}>
              <Plus className="w-3 h-3" /> New
            </button>
          )}
        </div>

        {creating && (
          <div className="mb-2 flex gap-1">
            <input className="border rounded px-2 py-1 text-sm flex-1" placeholder="Name…"
              value={newName} onChange={(e) => setNewName(e.target.value)} />
            <Button size="sm" onClick={saveDraft}>Save</Button>
            <Button size="sm" variant="outline"
              onClick={() => { setCreating(false); setNewName(''); }}>×</Button>
          </div>
        )}

        <div className="space-y-1 mb-3">
          {scenarios.length === 0 && !creating && (
            <div className="text-xs text-gray-500">No scenarios yet — click "New" or just toggle valves below for an ad-hoc solve.</div>
          )}
          {scenarios.map((sc) => (
            <div key={sc.id} className={`flex items-center justify-between px-2 py-1 rounded text-sm cursor-pointer ${
              activeScenarioId === sc.id ? 'bg-blue-50 border border-blue-300' : 'hover:bg-gray-50'
            }`} onClick={() => activate(sc)}>
              <span className="truncate">{sc.name}</span>
              <span className="text-xs text-gray-500">{sc.open_valve_node_ids.length}v</span>
              <button className="text-red-500 ml-1" onClick={(e) => { e.stopPropagation(); remove(sc.id); }}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>

        {/* Valve toggle list */}
        <div className="border-t pt-2">
          <div className="text-xs font-semibold mb-1">
            Open valves ({draftOpenValves.size}/{valves.length})
          </div>
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {valves.length === 0 && (
              <div className="text-xs text-gray-500">Add valve nodes to the network first.</div>
            )}
            {valves.map((v) => (
              <label key={v.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                <input type="checkbox" checked={draftOpenValves.has(v.id)}
                  onChange={() => toggleValve(v.id)} />
                <span className="flex-1 truncate">{v.name || `valve #${v.id}`}</span>
                {v.rated_valve_flow_lpm != null && (
                  <span className="text-gray-500">{v.rated_valve_flow_lpm} L/m</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-2 flex gap-1">
          <Button size="sm" onClick={solve} disabled={solving} className="flex-1">
            <Play className="w-4 h-4 mr-1" /> {solving ? 'Solving…' : 'Solve'}
          </Button>
          {(activeScenarioId || creating) && (
            <Button size="sm" variant="outline" onClick={saveDraft}>
              <Save className="w-4 h-4" />
            </Button>
          )}
        </div>

        {error && <div className="mt-2 text-red-600 text-xs">{error}</div>}

        {result && (
          <div className="mt-3 border-t pt-2 text-xs space-y-1">
            <div className="font-semibold">Result</div>
            <div className="text-gray-600">
              {result.meta.open_valves} valves · {result.meta.total_demand_lpm} L/min total
            </div>
            {result.warnings.length === 0 ? (
              <div className="text-green-700">All checks pass.</div>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {result.warnings.map((w, i) => (
                  <WarningRow key={i} w={w} onClick={() => onJump({ node_id: w.node_id, pipe_id: w.pipe_id })} />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WarningRow({ w, onClick }: { w: SolveWarning; onClick: () => void }) {
  const icon =
    w.level === 'error' ? <AlertCircle className="w-3 h-3 text-red-600" /> :
    w.level === 'warn'  ? <AlertTriangle className="w-3 h-3 text-amber-600" /> :
                          <Info className="w-3 h-3 text-blue-600" />;
  const cls = w.level === 'error' ? 'text-red-700' : w.level === 'warn' ? 'text-amber-700' : 'text-blue-700';
  const clickable = w.node_id || w.pipe_id;
  return (
    <button
      className={`flex items-start gap-1 text-left ${cls} ${clickable ? 'hover:underline' : 'cursor-default'}`}
      onClick={clickable ? onClick : undefined}
    >
      {icon}<span>{w.message}</span>
    </button>
  );
}
